<template>
  <ion-header>
    <ion-toolbar>
      <ion-title>{{ title }}</ion-title>
      <ion-buttons slot="start">
        <ion-button @click="cancel" color="danger">Cancel</ion-button>
      </ion-buttons>
      <ion-buttons slot="end">
        <ion-button @click="save">Save</ion-button>
      </ion-buttons>
    </ion-toolbar>
  </ion-header>
  <ion-content class="ion-padding">
    <slot v-if="error" name="error">
      <div class="error">
        <a @click="error = null">🅧 Close</a>

        <json-viewer v-if="error instanceof Object" :data="error" open />
        <div v-else>{{ error }}</div>
      </div>
    </slot>
    <slot name="top" />
    <json-viewer :data="{ defaultValues, formData, openapiSchema }" closed />
    <OpenApiForm
      :openapi-schema="openapiSchema"
      :ignored-fields="ignoredFields"
      :first-fields="firstFields"
      :default-values="formDefaults"
      :multiline-fields="multilineFields"
      :model-value="formData"
      @update:modelValue="(event) => applyData(event)"
    >
      <template v-slot:form="{ data, updateItem }"
        ><!-- this is of the OpenApiForm -->
        <slot
          name="form"
          :save="save"
          :data="data"
          :updateItem="updateItem"
        /><!-- this one will be set by our user -->
      </template>
    </OpenApiForm>
    <slot name="bottom" :save="save" />
  </ion-content>
</template>

<script setup lang="ts" generic="DATA extends dict">
import {
  IonContent,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonButton,
} from "@ionic/vue";
import OpenApiForm from "../OpenApiForm.vue";
import { computed, defineComponent, ref } from "vue";
import type { Ref } from "vue";
import type { dict } from "@/types";
import JsonViewer from "@/components/JsonViewer.vue";

defineComponent({
  name: "OpenApiModal",
});

const props = withDefaults(
  defineProps<{
    title: string;
    openapiSchema: object;
    ignoredFields: string[];
    firstFields: string[];
    multilineFields: string[];
    defaultValues: Partial<DATA>;
    /** Values on top of `defaultValues`, e.g. from a restored draft. Read once, when the form is created. */
    initialValues?: Partial<DATA>;
    doSave?: (data: DATA) => Promise<boolean>;
  }>(),
  {
    title: "",
    ignoredFields: () => [],
    firstFields: () => [],
    multilineFields: () => [],
    defaultValues: () => ({}),
  }
);
const emit = defineEmits<{
  (e: "close", value: { discard: boolean; data: DATA }): void;
  (e: "save", value: { discard: boolean; data: DATA }): void;
  (e: "saveCancelled", value: { discard: boolean; data: DATA }): void;
}>();

const formData: Ref<Partial<DATA>> = ref({ ...props.initialValues }); // using `Ref<…>` type instead of `ref<…>` to make `clone()` know the type
const error = ref<string | null | any>(null);
// OpenApiForm writes the fields it shows into the object it gets, so it must not get the store's object itself
const formDefaults = computed(() => ({ ...props.defaultValues, ...props.initialValues }));

function clone<T>(obj: Ref<T>): T {
  return JSON.parse(JSON.stringify(obj.value));
}

function applyData(data: DATA) {
  console.log("applyData", data);
  formData.value = data;
}

async function save() {
  const data = clone(formData);
  const func = props.doSave;
  if (func !== undefined) {
    try {
      const callbackIsOkay = await func(data);
      if (!callbackIsOkay) {
        emit("saveCancelled", { discard: false, data });
        error.value = "Callback returned false.";
        // TODO: toast
        return false;
      }
    } catch (e) {
      console.log("abort save due to error:", e);
      // TODO: toast
      emit("saveCancelled", { discard: false, data });
      error.value = e;
      return false;
    }
  }
  emit("save", { discard: false, data });
  return true;
}

function cancel() {
  emit("close", { discard: true, data: clone(formData) });
}

defineExpose({ formData });
</script>

<style scoped lang="scss">
div.error {
  background-color: #ffa5a5;
}
</style>
